const isDev = import.meta.env.DEV

import React from 'react'
import styled from 'styled-components'
import { Link } from '@reach/router'

// icons
// import { CheckCircleFill } from '@styled-icons/bootstrap/CheckCircleFill'
import { Label } from '@styled-icons/material/Label'
import { Clock } from '@styled-icons/fa-regular/Clock'
import { Calculator } from '@styled-icons/fa-solid/Calculator'

// utils
import th from 'utils/themeHelper'

// styles
import { getPillColors } from 'components/SearchResultsContainer/styles'
import DashCardWrapper from 'components/Dashboard/SearchResultsCards/DashCardWrapper'

// components
import LinkButton from '../SmallButtonPrimary/LinkButton'

const ActionLink = styled(Link)`
  display: inline-block;
  margin-right: 20px;
  text-decoration: none;

  @media screen and (max-width: 600px) {
    margin-bottom: 10px;
  }
`

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Actions = styled.div`
  margin: 10px 15px;
`

const PillWrapper = styled.div`
  margin-left: 8px;
  margin-top: -5px;
`

const Info = styled.div`
  display: block;
  margin: 10px 15px 0;
  flex: 1;
`

const InfoFieldLabel = styled.div`
  font-family: ${th('fonts.bold')};
  margin-right: 10px;
`

const InfoFieldValue = styled.div`
  color: ${props => (props.hasValue ? th('text.dark') : th('text.medium'))};
  font-family: ${props => (props.hasValue ? th('fonts.regular') : th('fonts.italic'))};
  margin-left: 30px;
`

const InfoFieldIcon = styled.div`
  margin-right: 10px;

  & > svg {
    height: 20px;
  }
`

const InfoGroup = styled.div`
  min-width: 30%;
`

const InfoGroupRow = styled.div`
  color: ${th('text.mid')};
  line-height: 1.25em;
  margin-bottom: 15px;
`

const InfoGroupRowInner = styled.div`
  display: flex;
  word-break: break-word;
`

const Pill = styled.div`
  background: ${props => props.bgColor || th('backgrounds.default')};
  color: ${props => props.fontColor || th('text.dark')};
  border-radius: 3px;
  display: inline-block;
  font-size: 9px;
  margin-top: 2px;
  padding: 2px 5px;
  text-align: center;
  text-transform: uppercase;
  width: ${props => (props.fullWidth ? '100%' : 'auto')};
`

const ShiftLogCard = ({
  break_summary_string: breakSummaryString,
  clock_in_status: clockInStatus,
  clock_in_time: clockInTime,
  clock_out_status: clockOutStatus,
  clock_out_time: clockOutTime,
  has_break_end: breakEnd,
  has_break_start: breakStart,
  last_log_id: entryId,
  last_log_status: lastLogStatus,
  last_log_type: lastLogType,
  participant_name: participantName,
  total_hours: totalHours
}) => {
  const { bgColor: clockInBg, color: clockInColor } = getPillColors({
    key: 'mobile_time_summary',
    value: clockInStatus
  })

  const { bgColor: clockOutBg, color: clockOutColor } = getPillColors({
    key: 'mobile_time_summary',
    value: clockOutStatus
  })

  const label = lastLogType === 'clock_out' ? 'Approve clock out' : 'Approve clock in'

  return (
    <DashCardWrapper>
      <ActionsWrapper>
        <Info>
          <InfoGroup>
            <InfoGroupRow>
              <InfoGroupRowInner>
                <InfoFieldIcon>
                  <Label />
                </InfoFieldIcon>
                <InfoFieldLabel>{participantName}</InfoFieldLabel>
              </InfoGroupRowInner>
            </InfoGroupRow>

            <InfoGroupRow>
              <InfoGroupRowInner>
                <InfoFieldIcon>
                  <Clock />
                </InfoFieldIcon>
                <InfoFieldLabel>Shift Start</InfoFieldLabel>
              </InfoGroupRowInner>

              <InfoGroupRowInner>
                <InfoFieldValue hasValue={clockInTime}>{clockInTime || 'N/A'}</InfoFieldValue>

                {clockInTime && (
                  <PillWrapper>
                    <Pill bgColor={clockInBg} fontColor={clockInColor}>
                      {clockInStatus}
                    </Pill>
                  </PillWrapper>
                )}
              </InfoGroupRowInner>
            </InfoGroupRow>

            <InfoGroupRow>
              <InfoGroupRowInner>
                <InfoFieldIcon>
                  <Clock />
                </InfoFieldIcon>
                <InfoFieldLabel>Shift Break</InfoFieldLabel>
              </InfoGroupRowInner>

              <InfoGroupRowInner>
                <InfoFieldValue hasValue={breakSummaryString}>
                  {breakSummaryString || 'N/A'}
                </InfoFieldValue>

                {breakStart && !breakEnd && (
                  <PillWrapper>
                    <Pill bgColor={'#B6BEFF'} fontColor={'#212121'}>
                      Ongoing
                    </Pill>
                  </PillWrapper>
                )}

                {breakEnd && (
                  <PillWrapper>
                    <Pill bgColor={'#2A9044'} fontColor={'white'}>
                      Ended
                    </Pill>
                  </PillWrapper>
                )}
              </InfoGroupRowInner>
            </InfoGroupRow>

            <InfoGroupRow>
              <InfoGroupRowInner>
                <InfoFieldIcon>
                  <Clock />
                </InfoFieldIcon>
                <InfoFieldLabel>Shift End</InfoFieldLabel>
              </InfoGroupRowInner>

              <InfoGroupRowInner>
                <InfoFieldValue hasValue={clockOutTime}>{clockOutTime || 'N/A'}</InfoFieldValue>

                {clockOutTime && (
                  <PillWrapper>
                    <Pill bgColor={clockOutBg} fontColor={clockOutColor}>
                      {clockOutStatus}
                    </Pill>
                  </PillWrapper>
                )}
              </InfoGroupRowInner>
            </InfoGroupRow>

            <InfoGroupRow>
              <InfoGroupRowInner>
                <InfoFieldIcon>
                  <Calculator />
                </InfoFieldIcon>
                <InfoFieldLabel>Total hours</InfoFieldLabel>
              </InfoGroupRowInner>

              <InfoGroupRowInner>
                <InfoFieldValue hasValue={totalHours}>{totalHours || 'N/A'}</InfoFieldValue>
              </InfoGroupRowInner>
            </InfoGroupRow>
          </InfoGroup>
        </Info>
      </ActionsWrapper>

      {lastLogStatus === 'Pending' && (
        <Actions>
          <ActionLink to={`${isDev ? '/timekeeper' : ''}?entryId=${entryId}`}>
            <LinkButton>{label}</LinkButton>
          </ActionLink>
        </Actions>
      )}
    </DashCardWrapper>
  )
}

export default ShiftLogCard
